import React, { Component } from 'react';
import styled from 'styled-components';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { Column, Row } from '@zocdoc-frontend-common/styles-grid';
import { coral, navy } from '@zocdoc/branding/colors';

export const INVALID_NAME_MESSAGE = 'Please type a name.';

/**
 * @param {string} firstName
 * @param {string} lastName
 * @param {function(firstName: string): void} onFirstNameChange
 * @param {function(): void} onFirstNameFocus
 * @param {function(lastName: string): void} onLastNameChange
 * @param {function(): void} onLastNameFocus
 * @param {boolean} showNameError
 * @returns {JSX.Element}
 */
export default class FirstLastName extends Component {
    static getDerivedStateFromProps(nextProps, prevState) {
        if (
            nextProps.showNameError &&
            nextProps.showNameError !== prevState.cachedShowNameError
        ) {
            return {
                cachedShowNameError: nextProps.showNameError,
                canShowNameError: true,
                showNameError: nextProps.showNameError,
            };
        }

        return null;
    }

    constructor(props, ...args) {
        super(props, ...args);

        this.state = {
            cachedShowNameError: props.showNameError, // eslint-disable-line react/no-unused-state
            canShowNameError: props.showNameError,
            firstName: props.firstName || '',
            firstNameHasBlurred: false,
            lastName: props.lastName || '',
            lastNameHasBlurred: false,
            showNameError: props.showNameError,
        };
    }

    render() {
        const { canShowNameError, firstName, lastName, showNameError } =
            this.state;

        return (
            <div>
                <Row>
                    <Column size={12}>
                        <StyledFirstNameTextBox
                            className="exclude-from-playback"
                            data-test="firstname"
                            data-uem-id="firstname"
                            invalid={showNameError && canShowNameError}
                            onBlur={this.handleFirstNameBlur}
                            onChange={this.handleFirstNameChange}
                            onFocus={this.props.onFirstNameFocus}
                            placeholder="First"
                            value={firstName}
                        />
                    </Column>
                    <Column size={12}>
                        <StyledTextBox
                            className="exclude-from-playback"
                            data-test="lastname"
                            data-uem-id="lastname"
                            invalid={showNameError && canShowNameError}
                            onBlur={this.handleLastNameBlur}
                            onChange={this.handleLastNameChange}
                            onFocus={this.props.onLastNameFocus}
                            placeholder="Last"
                            value={lastName}
                        />
                    </Column>
                </Row>
                <Row>
                    <ErrorMessage data-test="name-error">
                        {showNameError && canShowNameError
                            ? INVALID_NAME_MESSAGE
                            : undefined}
                    </ErrorMessage>
                </Row>
            </div>
        );
    }

    handleFirstNameChange = (e) => {
        const { value } = e.target;

        this.setState({
            showNameError: false,
            firstName: value,
        });

        this.props.onFirstNameChange(value);
    };

    handleLastNameChange = (e) => {
        const { value } = e.target;

        this.setState({
            showNameError: false,
            lastName: value,
        });

        this.props.onLastNameChange(value);
    };

    handleFirstNameBlur = () => {
        const { firstName, lastName, canShowNameError, lastNameHasBlurred } =
            this.state;

        if (!firstName || !lastName) {
            this.setState({
                showNameError: true,
                firstNameHasBlurred: true,
                canShowNameError: lastNameHasBlurred ? true : canShowNameError,
            });
        } else {
            this.setState({
                showNameError: false,
                firstNameHasBlurred: true,
                canShowNameError: lastNameHasBlurred ? true : canShowNameError,
            });
        }
    };

    handleLastNameBlur = () => {
        const { firstName, lastName, canShowNameError, firstNameHasBlurred } =
            this.state;

        if (!firstName || !lastName) {
            this.setState({
                showNameError: true,
                lastNameHasBlurred: true,
                canShowNameError: firstNameHasBlurred ? true : canShowNameError,
            });
        } else {
            this.setState({
                showNameError: false,
                lastNameHasBlurred: true,
                canShowNameError: firstNameHasBlurred ? true : canShowNameError,
            });
        }
    };
}

const StyledTextBox = styled(Textbox)`
    min-width: 0;
    ${(p) => (p.invalid ? `border-color: ${coral}` : '')};
`;

const StyledFirstNameTextBox = styled(StyledTextBox)`
    border-right: none;

    &:focus {
        border-right: solid;
        border-right-width: 1px;
        border-right-color: ${navy};
    }
`;
